import React, { MouseEventHandler } from 'react';
import Box from '@mui/material/Box';
import useStyles from './styles';
import { Button, primaryOutlinedButtonSx } from '@elevator/ui';
import clsx from 'clsx';

/**
 * Configuration for an action button (submit or cancel)
 */
export type ActionConfig = {
  /** The text label displayed on the button */
  label: string;
  /** Callback function executed when the button is clicked */
  onClick: (() => void) | MouseEventHandler;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Optional aria-label for accessibility */
  'aria-label'?: string;
};

/**
 * ActionButtons Component
 *
 * Renders a standard pair of form action buttons (Cancel and Submit) with responsive layout.
 * On desktop, buttons are displayed horizontally aligned to the right.
 * On mobile, buttons stay in one row and share the width equally (`flex: 1`).
 *
 * @param props - Component props
 * @param props.submit - Configuration for the submit button (contained variant, type="submit")
 * @param props.cancel - Configuration for the cancel button (outlined variant)
 * @param props.padding - When true, adds top and bottom padding (`theme.spacing(3)`) on the root (default: true)
 * @param props.align - Horizontal alignment of buttons: 'left', 'center', or 'right' (default: 'right')
 */
const ActionButtons = ({
  submit,
  cancel,
  padding = true,
  align = 'right',
}: ActionButtonsProps) => {
  const { classes } = useStyles();
  return (
    <Box
      className={clsx(
        classes.root,
        classes[
          `align${align.charAt(0).toUpperCase() + align.slice(1)}` as
            | 'alignRight'
            | 'alignLeft'
            | 'alignCenter'
        ],
        padding && classes.padding,
      )}
      data-component="ActionButtons"
    >
      {cancel && (
        <Button
          className={classes.button}
          variant={'outlined'}
          sx={primaryOutlinedButtonSx}
          size={'large'}
          onClick={cancel.onClick}
          disabled={cancel.disabled}
          aria-label={cancel['aria-label'] || cancel.label}
        >
          {cancel.label}
        </Button>
      )}
      {submit && (
        <Button
          className={classes.button}
          variant={'contained'}
          size={'large'}
          type={'submit'}
          onClick={submit.onClick}
          disabled={submit.disabled}
          aria-label={submit['aria-label'] || submit.label}
        >
          {submit.label}
        </Button>
      )}
    </Box>
  );
};

/**
 * Props for the ActionButtons component
 */
export type ActionButtonsProps = {
  /** Configuration for the submit button (contained variant, type="submit") */
  submit?: ActionConfig;
  /** Configuration for the cancel button (outlined variant) */
  cancel?: ActionConfig;
  /** When true, adds top and bottom padding (`theme.spacing(3)`) on the root */
  padding?: boolean;
  /** Horizontal alignment of buttons: 'left', 'center', or 'right' */
  align?: 'left' | 'center' | 'right';
};

export default ActionButtons;
